// src/components/tokenPage/performance/MovingAveragesCard.tsx

import React, { useMemo, useState } from "react";
import { MetricBox } from "@/components/tokenPage/shared/TokenDashboardComponents";
import { DataCard } from "@/components/ui";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { useTokenPrice } from "@/contexts/token";
import { sma, maDistancePercent, periodsAboveMAInRange } from "@/lib/metrics";

/**
 * Get ISO week number for a date (week starts on Monday)
 */
function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayOfWeek = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayOfWeek);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return weekNo;
}

/**
 * Convert daily price data to weekly closes (presentation-specific)
 */
function convertToWeeklyCloses(dates: string[], prices: number[]): number[] {
  const weekMap = new Map<string, { date: string; price: number }>();

  for (let i = 0; i < dates.length; i++) {
    const dateStr = dates[i]!;
    const price = prices[i]!;

    if (!Number.isFinite(price)) continue;

    const date = new Date(dateStr);
    const year = date.getUTCFullYear();
    const weekNum = getISOWeekNumber(date);
    const weekKey = `${year}-W${weekNum.toString().padStart(2, '0')}`;

    const existing = weekMap.get(weekKey);
    if (!existing || dateStr > existing.date) {
      weekMap.set(weekKey, { date: dateStr, price });
    }
  }

  const weekly = Array.from(weekMap.entries())
    .map(([, { date, price }]) => ({ date, price }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return weekly.map(w => w.price);
}

type RangeKey = '90d' | '180d' | '365d' | 'All';
type MAKey = 'MA20' | 'MA50' | 'MA200';

interface Metrics {
  ma20: number | null;
  ma50: number | null;
  ma200: number | null;
  distFromMA20: number | null;
  distFromMA50: number | null;
  distFromMA200: number | null;
  weeksAboveMA20: number | null;
  weeksAboveMA50: number | null;
  weeksAboveMA200: number | null;
  totalWeeks20: number | null;
  totalWeeks50: number | null;
  totalWeeks200: number | null;
}

/**
 * Moving Averages Card
 * 
 * Shows weekly moving averages and participation metrics.
 * Uses lib/metrics for MA calculations on weekly-converted data.
 */
const MovingAveragesCard: React.FC = () => {
  const { token, isLoading } = useTokenPrice();
  const prices = token.prices;
  const [range, setRange] = useState<RangeKey>('180d');
  const [selectedMA, setSelectedMA] = useState<MAKey>('MA50');

  const metrics = useMemo<Metrics>(() => {
    const empty: Metrics = {
      ma20: null,
      ma50: null,
      ma200: null,
      distFromMA20: null,
      distFromMA50: null,
      distFromMA200: null,
      weeksAboveMA20: null,
      weeksAboveMA50: null,
      weeksAboveMA200: null,
      totalWeeks20: null,
      totalWeeks50: null,
      totalWeeks200: null,
    };

    if (prices.values.length === 0) {
      return empty;
    }

    // Convert to weekly closes using price dates
    const weeklyPrices = convertToWeeklyCloses(prices.dates, prices.values);

    if (weeklyPrices.length === 0) {
      return empty;
    }

    // Calculate range in weeks
    const daysToWeeks: Record<RangeKey, number> = {
      '90d': Math.ceil(90 / 7),
      '180d': Math.ceil(180 / 7),
      '365d': Math.ceil(365 / 7),
      'All': weeklyPrices.length,
    };
    const weeksInRange = daysToWeeks[range];

    // Use lib/metrics for MA calculations
    const currentMA20 = sma(weeklyPrices, 20);
    const currentMA50 = sma(weeklyPrices, 50);
    const currentMA200 = sma(weeklyPrices, 200);

    // Use lib/metrics for distance calculations
    const distFromMA20 = maDistancePercent(weeklyPrices, 20);
    const distFromMA50 = maDistancePercent(weeklyPrices, 50);
    const distFromMA200 = maDistancePercent(weeklyPrices, 200);

    // Use lib/metrics for weeks above MA within range
    // Uses full history for MA calculation, but only counts within selected range
    const above20 = periodsAboveMAInRange(weeklyPrices, 20, weeksInRange);
    const above50 = periodsAboveMAInRange(weeklyPrices, 50, weeksInRange);
    const above200 = periodsAboveMAInRange(weeklyPrices, 200, weeksInRange);

    return {
      ma20: currentMA20,
      ma50: currentMA50,
      ma200: currentMA200,
      distFromMA20,
      distFromMA50,
      distFromMA200,
      weeksAboveMA20: above20?.above ?? null,
      weeksAboveMA50: above50?.above ?? null,
      weeksAboveMA200: above200?.above ?? null,
      totalWeeks20: above20?.total ?? null,
      totalWeeks50: above50?.total ?? null,
      totalWeeks200: above200?.total ?? null,
    };
  }, [prices.values, prices.dates, range]);

  // Get selected MA metrics
  const selectedMetrics = useMemo(() => {
    const window = selectedMA === 'MA20' ? 20 : selectedMA === 'MA50' ? 50 : 200;
    return {
      maValue: selectedMA === 'MA20' ? metrics.ma20 : selectedMA === 'MA50' ? metrics.ma50 : metrics.ma200,
      distFromMA: selectedMA === 'MA20' ? metrics.distFromMA20 : selectedMA === 'MA50' ? metrics.distFromMA50 : metrics.distFromMA200,
      weeksAbove: selectedMA === 'MA20' ? metrics.weeksAboveMA20 : selectedMA === 'MA50' ? metrics.weeksAboveMA50 : metrics.weeksAboveMA200,
      totalWeeks: selectedMA === 'MA20' ? metrics.totalWeeks20 : selectedMA === 'MA50' ? metrics.totalWeeks50 : metrics.totalWeeks200,
      window,
    };
  }, [selectedMA, metrics]);

  // Hide if no data
  const hasData = metrics.ma20 != null || metrics.ma50 != null || metrics.ma200 != null;

  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Moving Averages"
      subtitle="Weekly moving averages and participation"
      icon="trending-up"
      iconColor="text-blue-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={
        <div className="flex items-center gap-2">
          <TimeRangePicker
            options={['MA20', 'MA50', 'MA200'] as const}
            value={selectedMA}
            onChange={setSelectedMA}
            theme="blue"
          />
          <TimeRangePicker
            options={['90d', '180d', '365d', 'All'] as const}
            value={range}
            onChange={setRange}
            theme="blue"
          />
        </div>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <MetricBox
          label={`${selectedMA} (${selectedMetrics.window} week avg)`}
          value={selectedMetrics.maValue}
          fmt="price"
        />
        <MetricBox label={`Distance from ${selectedMA}`} value={selectedMetrics.distFromMA} fmt="pct1" toneBySign />
        <MetricBox label={`Weeks above ${selectedMA}`} value={selectedMetrics.weeksAbove} fmt="int" />
        <MetricBox label="Total weeks" value={selectedMetrics.totalWeeks} fmt="int" />
      </div>
    </DataCard>
  );
};

export default MovingAveragesCard;
